import React, { useState, useEffect, useRef } from 'react';

const SwipeMenu = ({ categories, currentPath, isAdmin }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const menuRef = useRef(null);
  const isDraggingRef = useRef(false);
  const hasMovedRef = useRef(false);
  
  const SWIPE_THRESHOLD = 100;
  const MENU_WIDTH = 256;
  const EDGE_THRESHOLD = 80;
  const ANGLE_THRESHOLD = 30;

  const calculateAngle = function(dx, dy) {
    return Math.abs(Math.atan2(dy, dx) * 180 / Math.PI);
  };

  useEffect(() => {
    const handleTouchStart = function(e) {
      const touch = e.touches[0];
      
      // При закрытом меню проверяем, что свайп начинается у края или внутри меню
      if (!isOpen && touch.clientX > EDGE_THRESHOLD && !menuRef.current.contains(e.target)) {
        return;
      }

      setStartX(touch.clientX);
      setStartY(touch.clientY);
      setCurrentX(touch.clientX);
      setCurrentY(touch.clientY);
      isDraggingRef.current = true;
      hasMovedRef.current = false;
    };

    const handleTouchMove = function(e) {
      if (!isDraggingRef.current) return;
      
      const touch = e.touches[0];
      setCurrentX(touch.clientX);
      setCurrentY(touch.clientY);
      
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      
      // Начало движения
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        hasMovedRef.current = true;
      }
      
      // Проверка угла свайпа
      const angle = calculateAngle(dx, dy);
      if (hasMovedRef.current && angle > ANGLE_THRESHOLD) {
        isDraggingRef.current = false;
        return;
      }
      
      let newX;
      if (isOpen) {
        // Для открытого меню: 0 это текущая позиция, ограничиваем движение влево
        newX = Math.min(0, dx);
      } else {
        // Для закрытого меню: -MENU_WIDTH это текущая позиция
        newX = Math.min(0, dx - MENU_WIDTH);
      }
      
      if (menuRef.current && hasMovedRef.current) {
        menuRef.current.style.transform = 'translateX(' + newX + 'px)';
        e.preventDefault();
      }
    };

    const handleTouchEnd = function() {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      
      if (!hasMovedRef.current) return;
      
      const diff = currentX - startX;
      
      // Открываем при свайпе вправо если меню закрыто
      if (!isOpen && diff > SWIPE_THRESHOLD) {
        setIsOpen(true);
      } 
      // Закрываем при свайпе влево если меню открыто
      else if (isOpen && diff < -SWIPE_THRESHOLD) {
        setIsOpen(false);
      }
      // Возвращаем в исходное положение
      else {
        if (menuRef.current) {
          menuRef.current.style.transform = isOpen ? 'translateX(0)' : 'translateX(-100%)';
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return function cleanup() {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isOpen, startX, startY, currentX, currentY]);

  useEffect(() => {
    if (menuRef.current) {
      menuRef.current.style.transform = isOpen ? 'translateX(0)' : 'translateX(-100%)';
    }
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';
  }, [isOpen]);

  return (
    <div className="lg:hidden">
      <div 
        className={'fixed inset-0 z-40 bg-slate-950/90 backdrop-blur-sm transition-opacity duration-300 ' + 
          (isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none')
        }
        onClick={function() { setIsOpen(false); }}
      />

      <div
        ref={menuRef}
        className="fixed inset-y-0 left-0 w-80 z-50 bg-slate-900 shadow-lg transform -translate-x-full transition-transform duration-300 ease-out touch-none"
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-slate-800/50">
            <div className="flex items-center">
              <span className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400">
                <a href="/">INSTATOOL</a>
              </span>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <a 
              href="/favorites"
              className={'flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-all duration-200 ' + 
                (currentPath === '/favorites' ? 'bg-slate-800/50 text-slate-200' : '')
              }
            >
              <i className="far fa-thumbs-up w-5" />
              <span>Избранное</span>
            </a>

            <a 
              href="/categories/new"
              className={'flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-all duration-200 ' +
                (currentPath === '/categories/new' ? 'bg-slate-800/50 text-slate-200' : '')
              }
            >
              <i className="fas fa-plus w-5" />
              <span>Добавить категорию</span>
            </a>

            {isAdmin && (
              <>
                <a 
                  href="/insta_accounts"
                  className={'flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-all duration-200 ' +
                    (currentPath === '/insta_accounts' ? 'bg-slate-800/50 text-slate-200' : '')
                  }
                >
                  <i className="fab fa-instagram w-5" />
                  <span>Доноры Instagram</span>
                </a>

                <a 
                  href="/proxies"
                  className={'flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-all duration-200 ' +
                    (currentPath === '/proxies' ? 'bg-slate-800/50 text-slate-200' : '')
                  }
                >
                  <i className="fas fa-network-wired w-5" />
                  <span>Прокси Oxylabs</span>
                </a>

                <a 
                  href="/users"
                  className={'flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-all duration-200 ' +
                    (currentPath === '/users' ? 'bg-slate-800/50 text-slate-200' : '')
                  }
                >
                  <i className="fas fa-users w-5" />
                  <span>Пользователи</span>
                </a>
              </>
            )}

            <div className="my-4 border-b border-slate-800/50" />

            {categories.map(function(cat) {
              return (
                <a
                  key={cat.id}
                  href={'/categories/' + cat.id}
                  className={'flex items-center justify-between px-4 py-3 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-all duration-200 ' +
                    (currentPath === '/categories/' + cat.id ? 'bg-slate-800/50 text-slate-200' : '')
                  }
                >
                  <span>{cat.name}</span>
                  <span className="text-sm px-2 py-1 rounded-full bg-slate-800/50">
                    {cat.accounts_count}
                  </span>
                </a>
              );
            })}
          </nav>

          <div className="p-4 border-t border-slate-800/50">
            <a 
              href="/users/sign_out"
              data-method="delete"
              className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-all duration-200"
            >
              <span>Выход</span>
            </a>
          </div>
        </div>

        <div className="absolute right-0 top-1/2 -translate-y-1/2 bg-slate-800/50 rounded-l-lg p-1">
          <div className="w-1 h-16 rounded-full bg-slate-600/50" />
        </div>
      </div>
    </div>
  );
};

export default SwipeMenu;